<template>
  <div class="plastic-dishes">
    <i-form
      v-scroll-to-invalid
      class="form"
      name="plastic-dishes-form"
      @submit="handleSubmit"
    >
      <div class="type">
        <strapi-label
          v-if="labels[PlasticDishesLabels.type]"
          :label="labels[PlasticDishesLabels.type]"
        />
        <div class="form-field-group">
          <!--type field-->
          <zaproo-select
            v-if="fields[PlasticDishesFields.type]"
            v-model="form.type"
            :removable="false"
            searchable
            highlight
            :no-result-label="$t('No results found')"
            no-result-class="text-neutral-900/50 text-center !py-2 !px-4"
            name="type"
            :placeholder="$t('Select option')"
            :rules="fieldRules[PlasticDishesFields.type]"
            dropdown-classes="py-2 mt-2"
            class="form-field checkmark"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[PlasticDishesFields.type]"
              />
            </template>

            <zaproo-select-option
              v-for="option in fields[PlasticDishesFields.type].options"
              :key="option.value"
              :value="option.value"
              :label="option.title"
            />
          </zaproo-select>
        </div>
      </div>

      <div class="dimensions mt-6">
        <strapi-label
          v-if="labels[PlasticDishesLabels.dimensions]"
          :label="labels[PlasticDishesLabels.dimensions]"
        />
        <div class="form-field-group">
          <!--size field-->
          <zaproo-select
            v-if="fields[PlasticDishesFields.size]"
            v-model="form.size"
            :removable="false"
            name="size"
            :placeholder="$t('Select option')"
            :rules="fieldRules[PlasticDishesFields.size]"
            dropdown-classes="py-2 mt-2"
            class="form-field checkmark"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[PlasticDishesFields.size]"
              />
            </template>

            <zaproo-select-option
              v-for="option in currentSizeOptions"
              :key="option.value"
              :value="option.value"
              >{{ option.title }}</zaproo-select-option
            >
          </zaproo-select>

          <!--otherSize field-->
          <zaproo-input
            v-if="isOtherSize && fields[PlasticDishesFields.otherSize]"
            v-model="form.otherSize"
            type="number"
            name="otherSize"
            :rules="fieldRules[PlasticDishesFields.otherSize]"
            :placeholder="fields[PlasticDishesFields.otherSize].placeholder"
            :description="fields[PlasticDishesFields.otherSize].description"
            class="form-field"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[PlasticDishesFields.otherSize]"
              />
            </template>
          </zaproo-input>
        </div>
      </div>

      <div class="material mt-6">
        <strapi-label
          v-if="labels[PlasticDishesLabels.color]"
          :label="labels[PlasticDishesLabels.color]"
        />
        <div class="form-field-group">
          <!--mainColor field-->
          <zaproo-input
            v-if="fields[PlasticDishesFields.mainColor]"
            v-model="form.mainColor"
            name="mainColor"
            :rules="fieldRules[PlasticDishesFields.mainColor]"
            :placeholder="fields[PlasticDishesFields.mainColor].placeholder"
            :description="fields[PlasticDishesFields.mainColor].description"
            class="form-field"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[PlasticDishesFields.mainColor]"
              />
            </template>
          </zaproo-input>
        </div>
      </div>

      <div class="print mt-6">
        <strapi-label
          v-if="labels[PlasticDishesLabels.print]"
          :label="labels[PlasticDishesLabels.print]"
        />
        <div class="form-field-group">
          <!--numberOfPrintColors field-->
          <zaproo-select
            v-if="fields[PlasticDishesFields.numberOfPrintColors]"
            v-model="form.numberOfPrintColors"
            :removable="false"
            :placeholder="$t('Select option')"
            :rules="fieldRules[PlasticDishesFields.numberOfPrintColors]"
            dropdown-classes="py-2 mt-2"
            name="numberOfPrintColors"
            class="form-field checkmark"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[PlasticDishesFields.numberOfPrintColors]"
              />
            </template>

            <zaproo-select-option
              v-for="option in fields[PlasticDishesFields.numberOfPrintColors]
                .options"
              :key="option.value"
              :value="option.value"
              >{{ option.title }}</zaproo-select-option
            >
          </zaproo-select>
        </div>

        <div class="form-field-group">
          <!--designFile field-->
          <zaproo-file-input
            v-if="
              isNumberOfPrintColors && fields[PlasticDishesFields.designFile]
            "
            v-model="form.designFile"
            class="mt-3"
            name="designFiles"
            :rules="fieldRules[PlasticDishesFields.designFile]"
            multiple
            :total-size-of-files="INPUT_FILE_TOTAL_SIZE"
            :placeholder="fields[PlasticDishesFields.designFile].placeholder"
            file-item-classes="h-10"
            remove-button-classes="!p-0"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[PlasticDishesFields.designFile]"
              />
            </template>
            <template #removeFileText>
              <icons-icon-trash />
              {{ $t("Remove") }}
            </template>
            <template #fileError="{ errors, clear }">
              <div class="mb-2 grid grid-cols-1 gap-2">
                <file-input-error-notification
                  :clear="clear"
                  :errors="errors"
                />
              </div>
            </template>
          </zaproo-file-input>
        </div>
      </div>

      <special-order-form-general-contacts-part
        v-model="form"
        class="general-contacts-form"
        :loading="loading"
      >
        <!--amountPcs field-->
        <zaproo-input
          v-if="fields[PlasticDishesFields.amountPcs]"
          v-model="form.amountPcs"
          type="number"
          name="amountPcs"
          :rules="fieldRules[PlasticDishesFields.amountPcs]"
          :placeholder="fields[PlasticDishesFields.amountPcs].placeholder"
          :description="fields[PlasticDishesFields.amountPcs].description"
          class="form-field"
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[PlasticDishesFields.amountPcs]"
            />
          </template>
        </zaproo-input>
      </special-order-form-general-contacts-part>
    </i-form>
  </div>
</template>

<script setup lang="ts">
import { Form as IForm, FormActions } from "vee-validate";
import SpecialOrderFormInputLabel from "../SpecialOrderFormInputLabel.vue";
import { DishSizes } from "./variables";
import { PlasticDishesFields, PlasticDishesLabels } from "./types";
import type {
  StrapiField,
  StrapiLabel as StrapiLabelType,
  StrapiSpecialOrders,
} from "~/types/strapi";

import { getFieldDefaultModel } from "~/modules/special-orders/helpers";

import StrapiLabel from "~/components/strapi/components/shared/StrapiLabel.vue";
import FileInputErrorNotification from "~/modules/special-orders/components/SpecialOrderForm/FileInputErrorNotification.vue";
import { INPUT_FILE_TOTAL_SIZE } from "~/modules/special-orders/variables";
import { useFormFieldCleaner } from "~/modules/special-orders/composables/useFormFieldCleaner";

const props = defineProps<{
  specialOrder: StrapiSpecialOrders;
}>();

const { collection: labelCollection } =
  useSpecialOrderUidDictionary<StrapiLabelType>(props.specialOrder.labels);

const { collection: fieldsCollection } =
  useSpecialOrderUidDictionary<StrapiField>(props.specialOrder.fields);

const { sendRequest, loading } = useSpecialOrdersSubmit(props.specialOrder);

const labels = computed(() => labelCollection(PlasticDishesLabels));
const fields = computed(() => fieldsCollection(PlasticDishesFields));
const fieldRules = useSpecialOrderFieldRules<typeof PlasticDishesFields>(
  fields.value,
);

const form = ref<ReturnType<typeof resetForm>>(resetForm());

const isOtherSize = computed(() => form.value.size === "other");

const isNumberOfPrintColors = computed(
  () => form.value.numberOfPrintColors !== "0",
);

const currentDishType = computed<string>(() => form.value.type ?? "");
const currentSizeOptions = computed(() => {
  const options = getDishOptions();
  const allDefaultsFalse = options.every((option) => !option.default);

  if (allDefaultsFalse && options.length) {
    options[0].default = true;
  }

  return options;
});

function resetForm() {
  return {
    type: getFieldDefaultModel(fields.value[PlasticDishesFields.type]),
    size: getFieldDefaultModel(fields.value[PlasticDishesFields.size]),
    otherSize: getFieldDefaultModel(
      fields.value[PlasticDishesFields.otherSize],
    ),
    mainColor: getFieldDefaultModel(
      fields.value[PlasticDishesFields.mainColor],
    ),
    numberOfPrintColors: getFieldDefaultModel(
      fields.value[PlasticDishesFields.numberOfPrintColors],
    ),
    amountPcs: getFieldDefaultModel(
      fields.value[PlasticDishesFields.amountPcs],
    ),
    designFile: [],
    email: "",
    phone: "",
    additionalInfo: "",
  };
}

function getDishOptions() {
  const dishSizes = fields.value[PlasticDishesFields.size];
  return dishSizes?.options
    ? dishSizes.options.filter((option) =>
        DishSizes[currentDishType.value].includes(option.value),
      )
    : [];
}

watch(
  () => form.value.type,
  () => {
    setDefaultSize();
  },
);

onMounted(() => {
  setDefaultSize();
});

function setDefaultSize(): void {
  form.value.size = currentSizeOptions.value.find((i) => i.default)?.value;
}

async function handleSubmit(
  _: Record<string, unknown>,
  { resetForm: reset }: FormActions<Record<string, unknown>>,
) {
  await sendRequest(form.value);
  reset();
  form.value = resetForm();
  setDefaultSize();
}

useFormFieldCleaner(
  {
    otherSize: {
      default: () =>
        getFieldDefaultModel(fields.value[PlasticDishesFields.otherSize]),
      ref: isOtherSize,
    },
    designFile: {
      default: () => [],
      ref: isNumberOfPrintColors,
    },
  },
  form,
);
</script>
